// Bot Arena (decision 99, Review 21): students don't play, they get the helper to write a bot. This
// is the game itself, Arena 1 "Coin Grab", used by the browser (tournaments, replays), the Worker
// (it re-scores every match it's sent from the moves alone) and the tests. It never runs a bot:
// bots are student code and only ever run inside the preview sandbox (apps/web/src/arena-runner.ts).
// The starter project carries the same rules as readable JavaScript for its practice match
// (packages/assignments/src/arena/engine-js.ts); a test plays both side by side so they can't drift.

/** What a bot can answer each turn. Anything else (or a crash, or no answer in time) is "stay". */
export type Dir = "up" | "down" | "left" | "right" | "stay";
export const DIRS: readonly Dir[] = ["up", "down", "left", "right", "stay"];

export interface Cell {
  x: number;
  y: number;
}

/** Coin Grab: a 9×9 board, 60 turns, 8 coins to start and 2 more every 10 turns. */
export const ARENA = {
  id: "coin-grab",
  title: "Coin Grab",
  size: 9,
  turns: 60,
  startPairs: 4,
  spawnEvery: 10,
  /** How long a bot gets to answer each move, in ms (a tournament stops a bot that takes longer). */
  moveMs: 250,
} as const;

/**
 * The walls. The board looks the same from both corners (each wall has a twin turned halfway
 * around the middle square), so neither corner is luckier. x goes right, y goes down, from 0.
 *   y0  . . . . . . . . .
 *   y1  . . # . . . . . .
 *   y2  . . # . . # # . .
 *   y3  . . . . . . . # .
 *   y4  . . . . # . . . .
 *   y5  . # . . . . . . .
 *   y6  . . # # . . # . .
 *   y7  . . . . . . # . .
 *   y8  . . . . . . . . .
 */
export const ARENA_WALLS: readonly Cell[] = (() => {
  const half: Cell[] = [
    { x: 2, y: 1 },
    { x: 2, y: 2 },
    { x: 5, y: 2 },
    { x: 6, y: 2 },
    { x: 1, y: 5 },
    { x: 4, y: 4 },
  ];
  const out: Cell[] = [];
  for (const c of half) for (const w of [c, mirror(c)]) if (!out.some((o) => o.x === w.x && o.y === w.y)) out.push(w);
  return out.sort((a, b) => a.y - b.y || a.x - b.x);
})();

/** The twin of a square, turned halfway around the middle (where the other bot's version of it is). */
export function mirror(c: Cell): Cell {
  return { x: ARENA.size - 1 - c.x, y: ARENA.size - 1 - c.y };
}

/** The rules in plain words, for the starter's PROJECT.md, the Home card and the replay page (grade 8 or below). */
export const ARENA_RULES: readonly string[] = [
  "Two bots start in opposite corners of a 9 by 9 board with some walls.",
  "There are 8 coins to start. Every 10 turns, 2 new coins appear.",
  "Each turn, both bots pick a move at the same time: up, down, left, right or stay.",
  "A move into a wall or off the edge means the bot stays put.",
  "Two bots can't share a square or pass through each other. If both try to go to the same square, or to swap places, neither one moves.",
  "Land on a coin to grab it.",
  "After 60 turns, the bot with more coins wins. The same number is a draw.",
  "A bot that crashes stays put that turn. A bot that takes too long stays put for the rest of the match.",
];

// ---- a match ----------------------------------------------------------------------------------

export interface BotPos {
  x: number;
  y: number;
  coins: number;
}

export interface MatchState {
  seed: number;
  /** Turns played so far (0 before the first). */
  turn: number;
  /** [the bot that starts top left, the bot that starts bottom right]. */
  bots: [BotPos, BotPos];
  coins: Cell[];
  /** The coin dice (mulberry32), so a match plays out the same way every time from its seed. */
  rng: number;
}

/** What a bot's move(state) gets, from its own side: "me" is always the bot being asked. */
export interface BotView {
  me: BotPos;
  other: BotPos;
  coins: Cell[];
  walls: Cell[];
  size: number;
  /** This turn, from 1. */
  turn: number;
  /** Turns left, counting this one. */
  turnsLeft: number;
  /** The bot's own notebook: an object that stays the same all match (the sandbox adds it, not the engine). */
  memory?: Record<string, unknown>;
}

const wallSet = new Set(ARENA_WALLS.map((w) => w.y * ARENA.size + w.x));
const key = (c: Cell) => c.y * ARENA.size + c.x;
export const isWall = (c: Cell): boolean => wallSet.has(key(c));
const inside = (c: Cell) => c.x >= 0 && c.y >= 0 && c.x < ARENA.size && c.y < ARENA.size;

/** A seeded random number in [0, 1) (mulberry32), stepping the match's dice. */
function roll(s: MatchState): number {
  s.rng = (s.rng + 0x6d2b79f5) | 0;
  let t = s.rng;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 0x100000000;
}

/** Puts down a coin and its twin on a free square (not a wall, a bot, a coin or the middle). False when none was found. */
function dropPair(s: MatchState): boolean {
  for (let tries = 0; tries < 100; tries++) {
    const c = { x: Math.floor(roll(s) * ARENA.size), y: Math.floor(roll(s) * ARENA.size) };
    const m = mirror(c);
    if (c.x === m.x && c.y === m.y) continue; // the middle square has no twin
    const taken = (p: Cell) => !inside(p) || isWall(p) || s.coins.some((k) => k.x === p.x && k.y === p.y) || s.bots.some((b) => b.x === p.x && b.y === p.y);
    if (taken(c) || taken(m)) continue;
    s.coins.push(c, m);
    return true;
  }
  return false;
}

export function newMatch(seed: number): MatchState {
  const end = ARENA.size - 1;
  const s: MatchState = { seed, turn: 0, bots: [{ x: 0, y: 0, coins: 0 }, { x: end, y: end, coins: 0 }], coins: [], rng: seed | 0 };
  for (let i = 0; i < ARENA.startPairs; i++) dropPair(s);
  return s;
}

export const matchOver = (s: MatchState): boolean => s.turn >= ARENA.turns;

/** The board as bot `who` (0 or 1) sees it. Copies, so a bot can't change the real match. */
export function botView(s: MatchState, who: 0 | 1): BotView {
  return {
    me: { ...s.bots[who] },
    other: { ...s.bots[who === 0 ? 1 : 0] },
    coins: s.coins.map((c) => ({ ...c })),
    walls: ARENA_WALLS.map((w) => ({ ...w })),
    size: ARENA.size,
    turn: s.turn + 1,
    turnsLeft: ARENA.turns - s.turn,
  };
}

/** A bot's answer as a move: "up", " Left ", "RIGHT" all count; anything else is "stay". */
export function cleanDir(answer: unknown): Dir {
  const d = typeof answer === "string" ? answer.trim().toLowerCase() : "";
  return (DIRS as readonly string[]).includes(d) ? (d as Dir) : "stay";
}

const STEP: Record<Dir, Cell> = { up: { x: 0, y: -1 }, down: { x: 0, y: 1 }, left: { x: -1, y: 0 }, right: { x: 1, y: 0 }, stay: { x: 0, y: 0 } };

/** What happened in one turn, for the replay's captions. */
export interface TurnEvents {
  /** Which bots grabbed a coin. */
  grabbed: [boolean, boolean];
  /** Both bots tried for the same square, or to swap places, so neither moved. */
  bumped: boolean;
  /** Which bots tried to move into a wall or off the board. */
  blocked: [boolean, boolean];
}

/** Plays one turn: both moves at once, then coins, then (every 10 turns) 2 new coins. Changes `s`. */
export function playTurn(s: MatchState, moves: [Dir, Dir]): TurnEvents {
  const blocked: [boolean, boolean] = [false, false];
  const target = s.bots.map((b, i) => {
    const d = STEP[moves[i]!];
    const t = { x: b.x + d.x, y: b.y + d.y };
    if (!inside(t) || isWall(t)) {
      blocked[i] = moves[i] !== "stay";
      return { x: b.x, y: b.y };
    }
    return t;
  }) as [Cell, Cell];
  const [a, b] = s.bots;
  const same = target[0].x === target[1].x && target[0].y === target[1].y;
  const swap = target[0].x === b.x && target[0].y === b.y && target[1].x === a.x && target[1].y === a.y;
  const bumped = same || swap;
  if (!bumped) {
    a.x = target[0].x;
    a.y = target[0].y;
    b.x = target[1].x;
    b.y = target[1].y;
  }
  const grabbed: [boolean, boolean] = [false, false];
  s.bots.forEach((bot, i) => {
    const at = s.coins.findIndex((c) => c.x === bot.x && c.y === bot.y);
    if (at >= 0) {
      s.coins.splice(at, 1);
      bot.coins++;
      grabbed[i] = true;
    }
  });
  s.turn++;
  if (s.turn % ARENA.spawnEvery === 0 && s.turn < ARENA.turns) dropPair(s);
  return { grabbed, bumped, blocked };
}

export type Winner = "a" | "b" | "draw";
export const winnerOf = (aCoins: number, bCoins: number): Winner => (aCoins > bCoins ? "a" : bCoins > aCoins ? "b" : "draw");

// ---- moves and replays ------------------------------------------------------------------------

const LETTER: Record<Dir, string> = { up: "U", down: "D", left: "L", right: "R", stay: "S" };
const FROM_LETTER: Record<string, Dir> = { U: "up", D: "down", L: "left", R: "right", S: "stay" };
export const encodeMoves = (moves: Dir[]): string => moves.map((m) => LETTER[m]).join("");
export const decodeMoves = (s: string): Dir[] => [...s].map((ch) => FROM_LETTER[ch] ?? "stay");
/** A full match's moves for one bot: one letter (U, D, L, R or S) per turn. */
export const MOVES_RE = new RegExp(`^[UDLRS]{${ARENA.turns}}$`);

/** Why a bot stood still: "broken" (bot.js didn't load or has no move function), "crash" (move threw) or "slow" (took too long; it stays for the rest of the match). */
export interface BotNote {
  kind: "broken" | "crash" | "slow";
  /** The first turn it happened on. */
  turn: number;
  /** How many turns it crashed on (crash only). */
  count?: number;
}

/** Everything needed to watch a match again without running any bot: the seed and both bots' moves. */
export interface Replay {
  v: 1;
  seed: number;
  a: string;
  b: string;
  notes?: { a?: BotNote; b?: BotNote };
}

export interface Frame {
  /** Turns played (0 is the start). */
  turn: number;
  bots: [BotPos, BotPos];
  coins: Cell[];
  /** The moves that led here (none at the start). */
  moves?: [Dir, Dir];
  events?: TurnEvents;
}

/** Every frame of a match from its replay: the start, then one per turn. */
export function replayFrames(r: Pick<Replay, "seed" | "a" | "b">): Frame[] {
  const s = newMatch(r.seed);
  const a = decodeMoves(r.a);
  const b = decodeMoves(r.b);
  const snap = (): Frame => ({ turn: s.turn, bots: [{ ...s.bots[0] }, { ...s.bots[1] }], coins: s.coins.map((c) => ({ ...c })) });
  const frames: Frame[] = [snap()];
  for (let t = 0; t < ARENA.turns; t++) {
    const moves: [Dir, Dir] = [a[t] ?? "stay", b[t] ?? "stay"];
    const events = playTurn(s, moves);
    frames.push({ ...snap(), moves, events });
  }
  return frames;
}

/** The final score from a replay (the Worker uses this rather than trusting the browser's count). */
export function scoreReplay(r: Pick<Replay, "seed" | "a" | "b">): { a: number; b: number; winner: Winner } {
  const frames = replayFrames(r);
  const last = frames[frames.length - 1]!;
  return { a: last.bots[0].coins, b: last.bots[1].coins, winner: winnerOf(last.bots[0].coins, last.bots[1].coins) };
}

// ---- playing a match with real bots -----------------------------------------------------------

/**
 * One bot in a match, as the tournament runner drives it (in the browser, each bot is its own
 * sandboxed frame). start() loads it (false: it's broken); move() asks for a turn's move and may
 * reject (the bot crashed) or never answer (the match's time limit catches that); stop() throws
 * it away, which is how a bot that took too long is stopped.
 */
export interface BotDriver {
  start(): Promise<boolean>;
  move(view: BotView): Promise<unknown>;
  stop(): void;
}

export interface MatchOptions {
  /** How long a bot gets to load. */
  startMs?: number;
  /** How long a bot gets for each move. */
  moveMs?: number;
  /** A crash after this long counts as too slow (default: half of moveMs). */
  slowMs?: number;
  /** Called after each turn (for a live view). */
  onTurn?: (s: MatchState, events: TurnEvents) => void;
}

const TIMED_OUT = Symbol("timed out");
function within<T>(p: Promise<T>, ms: number): Promise<T | typeof TIMED_OUT> {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([p, new Promise<typeof TIMED_OUT>((resolve) => (timer = setTimeout(() => resolve(TIMED_OUT), ms)))]).finally(() => clearTimeout(timer));
}

/**
 * Plays a whole match between two bots. A crash is "stay" for that turn; a bot that doesn't load,
 * or doesn't answer in time, is stopped and stays for the rest of the match (decision 99: an
 * infinite loop or a throw just means "stay").
 */
export async function playMatch(seed: number, drivers: [BotDriver, BotDriver], opts: MatchOptions = {}): Promise<Replay & { aCoins: number; bCoins: number; winner: Winner }> {
  const startMs = opts.startMs ?? 3000;
  const moveMs = opts.moveMs ?? ARENA.moveMs;
  const slowMs = opts.slowMs ?? moveMs / 2;
  const s = newMatch(seed);
  const notes: [BotNote | undefined, BotNote | undefined] = [undefined, undefined];
  const out: [Dir[], Dir[]] = [[], []];
  const live = [true, true];
  // One bot at a time, never both at once: sandboxed frames can share a browser process, so a slow
  // bot running alongside would make the other one look slow too. Both see the same board each
  // turn, and the moves still happen together in playTurn.
  const ask = async (i: 0 | 1): Promise<Dir> => {
    const d = drivers[i];
    if (!live[i]) return "stay";
    const asked = Date.now();
    const stuck = (): Dir => {
      live[i] = false;
      notes[i] = { kind: "slow", turn: s.turn + 1 };
      d.stop();
      return "stay";
    };
    try {
      const answer = await within(d.move(botView(s, i)), moveMs);
      return answer === TIMED_OUT ? stuck() : cleanDir(answer);
    } catch {
      // A loop the sandbox had to stop is a crash that took a long time: it counts as too slow,
      // so one runaway bot can't make every match it plays crawl.
      if (Date.now() - asked >= slowMs) return stuck();
      const n = notes[i];
      if (n?.kind === "crash") n.count = (n.count ?? 1) + 1;
      else if (!n) notes[i] = { kind: "crash", turn: s.turn + 1, count: 1 };
      return "stay";
    }
  };
  try {
    for (const i of [0, 1] as const) {
      const d = drivers[i];
      const ok = await within(d.start().catch(() => false), startMs);
      if (ok !== true) {
        live[i] = false;
        notes[i] = { kind: ok === TIMED_OUT ? "slow" : "broken", turn: 1 };
        d.stop();
      }
    }
    while (!matchOver(s)) {
      const moves: [Dir, Dir] = [await ask(0), await ask(1)];
      out[0].push(moves[0]);
      out[1].push(moves[1]);
      const events = playTurn(s, moves);
      opts.onTurn?.(s, events);
    }
  } finally {
    drivers.forEach((d, i) => live[i] && d.stop());
  }
  const replay: Replay = { v: 1, seed, a: encodeMoves(out[0]), b: encodeMoves(out[1]) };
  if (notes[0] || notes[1]) replay.notes = { ...(notes[0] ? { a: notes[0] } : {}), ...(notes[1] ? { b: notes[1] } : {}) };
  const [a, b] = s.bots;
  return { ...replay, aCoins: a.coins, bCoins: b.coins, winner: winnerOf(a.coins, b.coins) };
}

// ---- the sandbox side of a tournament ----------------------------------------------------------

/**
 * The page each bot plays from in a tournament: the bot's own bot.js and a tiny runner, built with
 * buildPreview (so it gets the preview's CSP and loop guards) and shown in an iframe with
 * PREVIEW_SANDBOX, never in the app's own origin. One frame per bot per match, so `memory` and any
 * globals start fresh each match. The runner answers { __arena: 1, kind: "move", id, dir } (or
 * crashed: true) for each { __arena: 1, kind: "move", id, view } from the app, and says
 * { kind: "ready", ok } once bot.js has loaded, and again for a { kind: "ping" } (ok: false when
 * there's no move function).
 */
export const BOT_RUNNER_JS = `(function () {
  var memory = {};
  var bot = typeof move === "function" ? move : null;
  var post = function (m) { m.__arena = 1; parent.postMessage(m, "*"); };
  window.addEventListener("message", function (e) {
    var m = e.data;
    if (e.source !== parent || !m || m.__arena !== 1) return;
    if (m.kind === "ping") return post({ kind: "ready", ok: !!bot });
    if (m.kind !== "move" || !bot) return;
    var view = m.view;
    view.memory = memory;
    try {
      var answer = bot(view);
      post({ kind: "move", id: m.id, dir: typeof answer === "string" ? answer.slice(0, 20) : null });
    } catch (err) {
      post({ kind: "move", id: m.id, crashed: true });
    }
  });
  post({ kind: "ready", ok: !!bot });
})();
`;

/** The files of a bot's runner page (give them to buildPreview). */
export function botRunnerFiles(code: string): Record<string, string> {
  return {
    "index.html": '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>Bot</title></head><body><script src="bot.js"></script><script src="runner.js"></script></body></html>',
    "bot.js": code,
    "runner.js": BOT_RUNNER_JS,
  };
}

/** A note in plain words for the replay: "Crashed on turn 12 (and 3 more turns)". */
export function noteText(n: BotNote): string {
  if (n.kind === "broken") return "Didn't start: bot.js has a mistake, or no move function.";
  if (n.kind === "slow") return `Took too long on turn ${n.turn}, so it stood still from then on.`;
  const more = (n.count ?? 1) - 1;
  return `Crashed on turn ${n.turn}${more > 0 ? ` (and ${more} more ${more === 1 ? "turn" : "turns"})` : ""}, so it stood still those turns.`;
}

// ---- tournaments ------------------------------------------------------------------------------

/** A match to play in a round robin: entry ids for the top-left (a) and bottom-right (b) bots. */
export interface Pairing {
  a: string;
  b: string;
  seed: number;
}

/** A seed for pair k of a tournament (any whole number in, a positive 31-bit seed out). */
export function pairSeed(tournamentSeed: number, k: number): number {
  let h = (tournamentSeed ^ Math.imul(k + 1, 0x9e3779b1)) | 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 1) || 1;
}

/** Everyone plays everyone twice on the same board, once from each corner. */
export function roundRobin(ids: string[], tournamentSeed: number): Pairing[] {
  const out: Pairing[] = [];
  let k = 0;
  for (let i = 0; i < ids.length; i++)
    for (let j = i + 1; j < ids.length; j++) {
      const seed = pairSeed(tournamentSeed, k++);
      out.push({ a: ids[i]!, b: ids[j]!, seed }, { a: ids[j]!, b: ids[i]!, seed });
    }
  return out;
}

export interface MatchScore {
  a: string;
  b: string;
  aCoins: number;
  bCoins: number;
}

export interface Standing {
  id: string;
  rank: number;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  /** Coins grabbed over all matches. */
  coins: number;
  /** 3 for a win, 1 for a draw. */
  points: number;
}

export const WIN_POINTS = 3;
export const DRAW_POINTS = 1;

/** The table: most points first, then most coins, then most wins. Same points, coins and wins share a rank. */
export function standings(ids: string[], matches: MatchScore[]): Standing[] {
  const by = new Map<string, Standing>(ids.map((id) => [id, { id, rank: 0, played: 0, wins: 0, draws: 0, losses: 0, coins: 0, points: 0 }]));
  for (const m of matches) {
    const a = by.get(m.a);
    const b = by.get(m.b);
    if (!a || !b) continue;
    const w = winnerOf(m.aCoins, m.bCoins);
    a.played++;
    b.played++;
    a.coins += m.aCoins;
    b.coins += m.bCoins;
    if (w === "draw") {
      a.draws++;
      b.draws++;
    } else {
      const [win, lose] = w === "a" ? [a, b] : [b, a];
      win.wins++;
      lose.losses++;
    }
  }
  const rows = [...by.values()].map((r) => ({ ...r, points: r.wins * WIN_POINTS + r.draws * DRAW_POINTS }));
  rows.sort((x, y) => y.points - x.points || y.coins - x.coins || y.wins - x.wins || x.id.localeCompare(y.id));
  rows.forEach((r, i) => {
    const prev = rows[i - 1];
    r.rank = prev && prev.points === r.points && prev.coins === r.coins && prev.wins === r.wins ? prev.rank : i + 1;
  });
  return rows;
}

// ---- house bots -------------------------------------------------------------------------------

/**
 * Built-in opponents: for practice in the starter project, and (if the teacher wants) in a
 * tournament so a small class has someone to play. Each is plain bot.js code like a student's,
 * so it runs the same way (in the sandbox) and gets no special powers.
 */
export interface HouseBot {
  id: string;
  name: string;
  /** One line for students: how it plays. */
  about: string;
  code: string;
}

export const WANDERER_JS = `// Wanderer: walks in a random direction, but never into a wall.
// Its dice start the same way every match, so practice games repeat.
function move(state) {
  var m = state.memory;
  if (m.dice === undefined) m.dice = 7;
  var options = ["up", "down", "left", "right"].filter(function (dir) {
    var x = state.me.x + (dir === "left" ? -1 : dir === "right" ? 1 : 0);
    var y = state.me.y + (dir === "up" ? -1 : dir === "down" ? 1 : 0);
    var wall = state.walls.some(function (w) { return w.x === x && w.y === y; });
    return x >= 0 && y >= 0 && x < state.size && y < state.size && !wall;
  });
  m.dice ^= m.dice << 13;
  m.dice ^= m.dice >>> 17;
  m.dice ^= m.dice << 5;
  m.dice = m.dice >>> 0;
  return options.length ? options[m.dice % options.length] : "stay";
}
`;

export const GREEDY_JS = `// Greedy: finds the closest coin by walking around walls, and takes the first step toward it.
function move(state) {
  var size = state.size;
  var blocked = {};
  state.walls.forEach(function (w) { blocked[w.x + "," + w.y] = true; });
  var coins = {};
  state.coins.forEach(function (c) { coins[c.x + "," + c.y] = true; });
  var steps = [["up", 0, -1], ["down", 0, 1], ["left", -1, 0], ["right", 1, 0]];
  var seen = {};
  seen[state.me.x + "," + state.me.y] = true;
  var queue = [];
  steps.forEach(function (s) { queue.push({ x: state.me.x + s[1], y: state.me.y + s[2], first: s[0] }); });
  while (queue.length) {
    var p = queue.shift();
    var k = p.x + "," + p.y;
    if (p.x < 0 || p.y < 0 || p.x >= size || p.y >= size || blocked[k] || seen[k]) continue;
    seen[k] = true;
    if (coins[k]) return p.first;
    steps.forEach(function (s) { queue.push({ x: p.x + s[1], y: p.y + s[2], first: p.first }); });
  }
  return "stay";
}
`;

export const HOUSE_BOTS: readonly HouseBot[] = [
  { id: "house:wanderer", name: "Wanderer", about: "Walks around at random, never into a wall.", code: WANDERER_JS },
  { id: "house:greedy", name: "Greedy", about: "Always goes for the closest coin.", code: GREEDY_JS },
];
export const houseBot = (id: string): HouseBot | undefined => HOUSE_BOTS.find((h) => h.id === id);
